import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast,
  SimpleGrid,
  Text,
  NumberInput,
  NumberInputField,
  IconButton,
  Divider,
  Image,
  FormHelperText,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import DateRangePicker from '../../components/common/DateRangePicker';
import {
  adminListSweepstakes,
  adminCreateSweepstake,
  adminUpdateSweepstake,
  adminDeleteSweepstake,
  adminListEntries,
  adminListWinners,
  adminFinalizeSweepstake,
  Sweepstake,
  adminListPrizes,
  adminCreatePrize,
  adminUpdatePrize,
  adminDeletePrize,
  adminReorderPrizes,
  SweepstakePrize,
} from '../../services/sweepstakes';
import { AddIcon, ArrowUpIcon, ArrowDownIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { uploadFile, createArticle } from '../../services/articles';
import { getImageUrl } from '../../utils/imageUtils';
import SaveStatusIndicator from '../../components/common/SaveStatusIndicator';
import { useAutoSave, loadDraft } from '../../hooks/useAutoSave';
import UploadPanel, { UploadPanelFile } from '../../components/common/UploadPanel';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';

const fmt = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('cs-CZ');
};

const defaultForm = {
  title: '',
  description: '',
  image_url: '',
  rules_url: '',
  start_at: '',
  end_at: '',
  picker_style: 'cycler',
  total_prizes: 1,
  prize_summary: '',
  entry_cost_points: 0,
  max_entries_per_user: 1,
};

const SweepstakesAdminPage: React.FC = () => {
  const toast = useToast();
  const [items, setItems] = useState<Sweepstake[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [status, setStatus] = useState<string>('');
  const { confirm } = useConfirmDialog();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState<any>(defaultForm);
  const [editing, setEditing] = useState<Sweepstake | null>(null);
  const editingRef = useRef<Sweepstake | null>(null);
  useEffect(() => { editingRef.current = editing; }, [editing]);
  const [draftKey, setDraftKey] = useState<string>('draft-sweepstake-new');
  const [showDraftRecovery, setShowDraftRecovery] = useState<boolean>(false);
  const [pendingDraft, setPendingDraft] = useState<any | null>(null);

  // Prizes state (integrated tab)
  const [prizes, setPrizes] = useState<SweepstakePrize[]>([]);
  const [prizeForm, setPrizeForm] = useState<{ name: string; quantity: number | string; image_url?: string; kind?: 'physical'|'points'|'xp'|'points_xp'; points?: number; xp?: number; description?: string }>(() => ({ name: '', quantity: 1, image_url: '', kind: 'physical', points: 0, xp: 0, description: '' }));
  const [savingPrize, setSavingPrize] = useState<boolean>(false);

  const { isOpen: isPrizeEditOpen, onOpen: onOpenPrizeEdit, onClose: onClosePrizeEdit } = useDisclosure();
  const [editingPrize, setEditingPrize] = useState<SweepstakePrize | null>(null);
  const [prizeEditForm, setPrizeEditForm] = useState<{ name: string; quantity: number | string; image_url?: string; kind?: 'physical'|'points'|'xp'|'points_xp'; points?: number; xp?: number; description?: string }>({ name: '', quantity: 1, image_url: '', kind: 'physical', points: 0, xp: 0, description: '' });
  const [activeTab, setActiveTab] = useState<number>(0);
  const [coverPreview, setCoverPreview] = useState<string>('');

  const uploadCoverImage = async (file: File): Promise<UploadPanelFile> => {
    const res = await uploadFile(file);
    setForm((prev: any) => ({ ...prev, image_url: res.url }));
    return {
      url: res.url,
      name: file.name,
      type: file.type || (res as any).type || 'image/*',
      size: file.size || (res as any).size || 0,
    };
  };

  // Build normalized payload for backend
  const buildPayload = (data: any) => {
    const now = new Date();
    const s = data.start_at ? new Date(data.start_at) : new Date(now.getTime() + 60 * 60 * 1000);
    const e = data.end_at ? new Date(data.end_at) : new Date(s.getTime() + 14 * 24 * 60 * 60 * 1000);
    const tpRaw = Number(data.total_prizes || 1);
    const tp = Number.isFinite(tpRaw) ? Math.floor(tpRaw) : 1;
    const total_prizes = tp < 1 ? 1 : (tp > 100 ? 100 : tp);
    const entry_cost_points = Math.max(0, Number(data.entry_cost_points) || 0);
    const max_entries_per_user = Math.max(1, Number(data.max_entries_per_user) || 1);
    return {
      title: (data.title || '').trim() || 'Nová soutěž',
      description: data.description || '',
      image_url: data.image_url || '',
      rules_url: data.rules_url || '',
      picker_style: data.picker_style || 'cycler',
      prize_summary: data.prize_summary || '',
      total_prizes,
      entry_cost_points,
      max_entries_per_user,
      start_at: isNaN(s.getTime()) ? new Date(now.getTime() + 60 * 60 * 1000).toISOString() : s.toISOString(),
      end_at: isNaN(e.getTime()) ? new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000).toISOString() : e.toISOString(),
    } as any;
  };

  // Auto-save (concept) like Articles
  const { saveStatus, lastSaved, forceSave, clearDraft, hasDraft, draftAge } = useAutoSave<any>({
    data: { id: (editing as any)?.id, ...form },
    storageKey: draftKey,
    onSave: async (data) => {
      const payload = buildPayload(data);
      if (data.id) {
        const res = await adminUpdateSweepstake(data.id, payload);
        return res;
      }
      if ((data.title || '').trim()) {
        const created = await adminCreateSweepstake(payload);
        setEditing(created);
        setDraftKey(`draft-sweepstake-${created.id}`);
        try { localStorage.removeItem('draft-sweepstake-new'); } catch {}
        return created;
      }
      return {} as any;
    },
    debounceMs: 2000,
    enabled: isOpen,
  });

  // Ensure sweepstake exists (auto-create draft) so prizes can be added without manual save
  const ensureCreated = async (): Promise<Sweepstake | null> => {
    try {
      if (editingRef.current) return editingRef.current;
      // Try auto-save first to create the draft
      await forceSave();
      // Wait briefly for editing to be set by auto-save
      const startWait = Date.now();
      while (!editingRef.current && Date.now() - startWait < 1500) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 100));
      }
      if (editingRef.current) return editingRef.current;
      // Fallback: create explicitly
      const payload = buildPayload(form);
      const created = await adminCreateSweepstake(payload);
      setEditing(created);
      setDraftKey(`draft-sweepstake-${created.id}`);
      try { setPrizes(await adminListPrizes(created.id)); } catch { setPrizes([]); }
      setActiveTab(2);
      toast({ status: 'success', title: 'Koncept soutěže vytvořen' });
      return created;
    } catch {
      toast({ status: 'error', title: 'Nelze automaticky vytvořit soutěž' });
      return null;
    }
  };

  const uploadRulesFile = async (file: File): Promise<UploadPanelFile> => {
    const res = await uploadFile(file);
    setForm((prev: any) => ({ ...prev, rules_url: res.url }));
    return {
      url: res.url,
      name: file.name,
      type: file.type || (res as any).type || 'application/octet-stream',
      size: file.size || (res as any).size || 0,
    };
  };

  const onCreateRulesArticle = async () => {
    try {
      const title = (form.title ? `Pravidla soutěže: ${form.title}` : 'Pravidla soutěže').trim();
      const a = await createArticle({ title, content: '<p>Zde doplňte pravidla soutěže.</p>', published: true });
      const url = `/articles/${a.id}`;
      setForm((prev: any) => ({ ...prev, rules_url: url }));
      toast({ status: 'success', title: 'Stránka pravidel vytvořena' });
    } catch (e: any) {
      toast({ status: 'error', title: 'Nelze vytvořit stránku pravidel' });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminListSweepstakes(status ? { status } : undefined);
      setItems(res);
    } finally {
      setLoading(false);
    }
  };

  const openPrizes = async (it: Sweepstake) => {
    openEdit(it);
    setActiveTab(2);
    try { setPrizes(await adminListPrizes(it.id)); } catch { setPrizes([]); }
  };

  const addPrize = async () => {
    if (!editing) { toast({ status: 'info', title: 'Uložte soutěž a poté přidejte výhry' }); return; }
    if (!prizeForm.name.trim()) { toast({ status: 'error', title: 'Název výhry je povinný' }); return; }
    try {
      setSavingPrize(true);
      await adminCreatePrize(editing.id, { name: prizeForm.name, quantity: Math.max(1, parseInt(String(prizeForm.quantity || '1'), 10) || 1), image_url: prizeForm.image_url, display_order: prizes.length, kind: prizeForm.kind, points: prizeForm.points, xp: prizeForm.xp, description: prizeForm.description });
      setPrizeForm({ name: '', quantity: 1, image_url: '', kind: 'physical', points: 0, xp: 0, description: '' });
      setPrizes(await adminListPrizes(editing.id));
    } catch (e:any) {
      toast({ status: 'error', title: 'Nelze uložit výhru' });
    } finally {
      setSavingPrize(false);
    }
  };

  const delPrize = async (p: SweepstakePrize) => {
    if (!editing) return;
    const ok = await confirm({
      title: 'Smazat výhru',
      message: 'Smazat výhru?',
      confirmText: 'Smazat',
      cancelText: 'Zrušit',
      isDanger: true,
    });
    if (!ok) return;
    await adminDeletePrize(editing.id, p.id as any);
    setPrizes(await adminListPrizes(editing.id));
  };

  const movePrize = async (idx: number, dir: -1 | 1) => {
    if (!editing) return;
    const arr = [...prizes];
    const ni = idx + dir;
    if (ni < 0 || ni >= arr.length) return;
    const tmp = arr[idx];
    arr[idx] = arr[ni];
    arr[ni] = tmp;
    setPrizes(arr);
    await adminReorderPrizes(editing.id, arr.map(p => p.id as any));
  };

  const startEditPrize = (p: SweepstakePrize) => {
    setEditingPrize(p);
    setPrizeEditForm({
      name: p.name || '',
      quantity: p.quantity || 1,
      image_url: p.image_url || '',
      kind: (p.kind as any) || 'physical',
      points: p.points || 0,
      xp: p.xp || 0,
      description: p.description || '',
    });
    onOpenPrizeEdit();
  };

  const saveEditPrize = async () => {
    if (!editing || !editingPrize) return;
    try {
      setSavingPrize(true);
      await adminUpdatePrize(editing.id, editingPrize.id as any, {
        name: prizeEditForm.name,
        quantity: Math.max(1, parseInt(String(prizeEditForm.quantity || '1'), 10) || 1),
        image_url: prizeEditForm.image_url,
        kind: prizeEditForm.kind,
        points: prizeEditForm.points,
        xp: prizeEditForm.xp,
        description: prizeEditForm.description,
      });
      toast({ status: 'success', title: 'Výhra upravena' });
      onClosePrizeEdit();
      setEditingPrize(null);
      setPrizes(await adminListPrizes(editing.id));
    } catch (e:any) {
      toast({ status: 'error', title: 'Nelze upravit výhru' });
    } finally {
      setSavingPrize(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  const openCreate = () => { setEditing(null); setForm(defaultForm); setPrizes([]); setActiveTab(0); onOpen(); };
  const openEdit = (it: Sweepstake) => {
    setEditing(it);
    setForm({
      title: it.title,
      description: it.description || '',
      image_url: (it as any).image_url || '',
      rules_url: (it as any).rules_url || '',
      start_at: (it as any).start_at ? String((it as any).start_at).slice(0, 16) : '',
      end_at: (it as any).end_at ? String((it as any).end_at).slice(0, 16) : '',
      picker_style: (it as any).picker_style || 'wheel',
      total_prizes: (it as any).total_prizes || 1,
      prize_summary: (it as any).prize_summary || '',
      entry_cost_points: (it as any).entry_cost_points ?? 0,
      max_entries_per_user: (it as any).max_entries_per_user ?? 1,
    });
    setActiveTab(0);
    onOpen();
    (async ()=>{ try { setPrizes(await adminListPrizes(it.id)); } catch { setPrizes([]); } })();
  };

  // Override open handlers to manage draft keys and recovery
  const openCreateWithDraft = () => {
    const key = 'draft-sweepstake-new';
    setDraftKey(key);
    const d = loadDraft<any>(key);
    if (d && Object.keys(d || {}).length) {
      setPendingDraft(d);
      setShowDraftRecovery(true);
    } else {
      setPendingDraft(null);
      setShowDraftRecovery(false);
    }
    openCreate();
  };
  const openEditWithDraft = (it: Sweepstake) => {
    setDraftKey(`draft-sweepstake-${it.id}`);
    const d = loadDraft<any>(`draft-sweepstake-${it.id}`);
    if (d && Object.keys(d || {}).length) {
      setPendingDraft(d);
      setShowDraftRecovery(true);
    } else {
      setPendingDraft(null);
      setShowDraftRecovery(false);
    }
    openEdit(it);
  };

  const save = async () => {
    try {
      if (!form.title || !form.start_at || !form.end_at) {
        toast({ status: 'error', title: 'Vyplňte název a datumy' });
        return;
      }
      const tpRaw = Number(form.total_prizes || 1);
      const tp = Number.isFinite(tpRaw) ? Math.floor(tpRaw) : 1;
      const total_prizes = tp < 1 ? 1 : (tp > 100 ? 100 : tp);
      const entry_cost_points = Math.max(0, Number(form.entry_cost_points) || 0);
      const max_entries_per_user = Math.max(1, Number(form.max_entries_per_user) || 1);
      // Normalize datetime-local (YYYY-MM-DDTHH:mm) to RFC3339 with timezone for Go backend
      const s = new Date(form.start_at);
      const e = new Date(form.end_at);
      const payload = {
        ...form,
        total_prizes,
        entry_cost_points,
        max_entries_per_user,
        start_at: isNaN(s.getTime()) ? form.start_at : s.toISOString(),
        end_at: isNaN(e.getTime()) ? form.end_at : e.toISOString(),
      };
      if (editing) {
        await adminUpdateSweepstake(editing.id, payload);
        toast({ status: 'success', title: 'Uloženo' });
        onClose();
        await load();
      } else {
        const created = await adminCreateSweepstake(payload);
        toast({ status: 'success', title: 'Vytvořeno', description: 'Nyní můžete přidat výhry' });
        setEditing(created);
        setActiveTab(2);
        try { setPrizes(await adminListPrizes(created.id)); } catch { setPrizes([]); }
        await load();
      }
    } catch (e: any) {
      toast({ status: 'error', title: 'Chyba', description: e?.response?.data?.error || 'Operace selhala' });
    }
  };

  const finalize = async (it: Sweepstake) => {
    const ok = await confirm({
      title: 'Spustit losování',
      message: 'Spustit losování a vybrat výherce?',
      confirmText: 'Losovat',
      cancelText: 'Zrušit',
      isDanger: true,
    });
    if (!ok) return;
    try { await adminFinalizeSweepstake(it.id); toast({ status: 'success', title: 'Losování dokončeno' }); await load(); }
    catch (e: any) { toast({ status: 'error', title: 'Chyba', description: e?.response?.data?.error || 'Nelze dokončit' }); }
  };

  const remove = async (it: Sweepstake) => {
    const ok = await confirm({
      title: 'Smazat soutěž',
      message: 'Smazat soutěž?',
      confirmText: 'Smazat',
      cancelText: 'Zrušit',
      isDanger: true,
    });
    if (!ok) return;
    try { await adminDeleteSweepstake(it.id); toast({ status: 'success', title: 'Smazáno' }); await load(); }
    catch (e: any) { toast({ status: 'error', title: 'Chyba', description: e?.response?.data?.error || 'Nelze smazat' }); }
  };

  const statusBadge = (s: string) => {
    const map: any = { draft: 'gray', scheduled: 'purple', active: 'green', locked: 'orange', finalized: 'blue', archived: 'red' };
    return <Badge colorScheme={map[s] || 'gray'}>{s}</Badge>;
  };

  return (
    <AdminLayout>
      <Container maxW="7xl" py={8}>
        <HStack justify="space-between" mb={4} flexWrap="wrap">
          <Heading size="lg">Soutěže</Heading>
          <HStack flexWrap="wrap">
            <Select value={status} onChange={(e)=>setStatus(e.target.value)} size="sm" maxW="220px">
              <option value="">Všechny</option>
              <option value="draft">Koncepty</option>
              <option value="scheduled">Naplánované</option>
              <option value="active">Aktivní</option>
              <option value="finalized">Dokončené</option>
              <option value="archived">Archiv</option>
            </Select>
            <Button colorScheme="blue" onClick={openCreateWithDraft} minW="max-content">Nová soutěž</Button>
          </HStack>
        </HStack>

        {loading ? (
          <Text>Načítám…</Text>
        ) : (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Název</Th>
                  <Th>Období</Th>
                  <Th>Stav</Th>
                  <Th>Výhry</Th>
                  <Th>Akce</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((it) => (
                  <Tr key={it.id}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold">{it.title}</Text>
                        {it.prize_summary && <Text fontSize="xs" opacity={0.8}>{it.prize_summary}</Text>}
                        <HStack spacing={2} wrap="wrap">
                          <Badge colorScheme={(it as any).entry_cost_points ? 'purple' : 'green'} fontSize="0.7rem">
                            {(it as any).entry_cost_points ? `Vstup: ${(it as any).entry_cost_points} bodů` : 'Vstup: zdarma'}
                          </Badge>
                          {(it as any).max_entries_per_user > 1 && (
                            <Badge colorScheme="gray" fontSize="0.7rem">max {(it as any).max_entries_per_user}×/osoba</Badge>
                          )}
                        </HStack>
                      </VStack>
                    </Td>
                    <Td>{fmt((it as any).start_at)} – {fmt((it as any).end_at)}</Td>
                    <Td>{statusBadge(it.status)}</Td>
                    <Td>{(it as any).total_prizes || '-'}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button size="xs" variant="outline" onClick={()=>openEditWithDraft(it)}>Upravit</Button>
                        <Button size="xs" variant="outline" onClick={()=>openPrizes(it)}>Výhry</Button>
                        <Button size="xs" as={RouterLink} to={`/admin/sweepstakes/${it.id}/visual`} variant="outline">Vizualizace</Button>
                        <Button size="xs" variant="outline" onClick={()=>finalize(it)} isDisabled={it.status === 'finalized'}>Losovat</Button>
                        <Button size="xs" colorScheme="red" variant="outline" onClick={()=>remove(it)}>Smazat</Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* Create/Edit Modal with tabs */}
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "full", lg: "full" }} scrollBehavior="inside" isCentered={false}>
          <ModalOverlay />
          <ModalContent maxW="none" w={{ base: "100%", md: "95vw", lg: "90vw" }} h="90vh" mx={4}>
            <ModalHeader>
              <HStack justify="space-between" flexWrap="wrap">
                <Text fontSize="lg" fontWeight="bold">{editing ? 'Upravit soutěž' : 'Nová soutěž'}</Text>
                <HStack spacing={3} flexShrink={0}>
                  <SaveStatusIndicator status={saveStatus} lastSaved={lastSaved} compact />
                  <Button size="sm" variant="outline" onClick={forceSave}>Uložit nyní</Button>
                </HStack>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody minH="400px" maxH="calc(90vh - 120px)">
              {showDraftRecovery && (
                <Box mb={3} p={3} borderRadius="md" bg="yellow.50" borderWidth="1px" borderColor="yellow.200">
                  <HStack justify="space-between" align="center">
                    <Text>Byl nalezen místní koncept{draftAge!=null ? ` (před ${draftAge} min)` : ''}. Chcete jej obnovit?</Text>
                    <HStack>
                      <Button size="sm" onClick={() => {
                        if (pendingDraft) {
                          const d: any = pendingDraft;
                          setForm((prev: any) => ({ ...prev, ...d }));
                        }
                        setShowDraftRecovery(false);
                      }}>Obnovit</Button>
                      <Button size="sm" variant="outline" onClick={() => { clearDraft(); setShowDraftRecovery(false); setPendingDraft(null); }}>Zahodit</Button>
                    </HStack>
                  </HStack>
                </Box>
              )}
              <Tabs index={activeTab} onChange={setActiveTab as any} isFitted>
                <TabList>
                  <Tab>Základní</Tab>
                  <Tab>Termíny a limity</Tab>
                  <Tab>Výhry</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired>
                        <FormLabel>Název</FormLabel>
                        <Input value={form.title} onChange={(e)=>setForm({ ...form, title: e.target.value })} />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Popis</FormLabel>
                        <Textarea value={form.description} onChange={(e)=>setForm({ ...form, description: e.target.value })} />
                      </FormControl>
                      <SimpleGrid columns={2} spacing={4}>
                        <FormControl>
                          <FormLabel>Titulní obrázek</FormLabel>
                          <VStack align="start" spacing={2}>
                            <UploadPanel
                              label="Titulní obrázek soutěže"
                              description="Nahrajte nebo přetáhněte titulní obrázek soutěže."
                              accept="image/*"
                              multiple={false}
                              maxFiles={1}
                              uploadFn={uploadCoverImage}
                              allowUrlImport
                              urlPlaceholder="/uploads/... nebo https://example.com/cover.jpg"
                              onChange={(files) => {
                                const last = files[files.length - 1];
                                if (last?.url) {
                                  setCoverPreview('');
                                  setForm((prev: any) => ({ ...prev, image_url: last.url }));
                                }
                              }}
                            />
                            <HStack>
                              <Image src={coverPreview || getImageUrl(form.image_url) || '/dist/img/logo-club-empty.svg'} alt="cover" boxSize="80px" objectFit="cover" borderRadius="md" />
                              {form.image_url && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setForm((prev: any) => ({ ...prev, image_url: '' }));
                                    setCoverPreview('');
                                  }}
                                >
                                  Odebrat
                                </Button>
                              )}
                            </HStack>
                            <Input placeholder="nebo vložte URL" value={form.image_url} onChange={(e)=>setForm({ ...form, image_url: e.target.value })} />
                          </VStack>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Pravidla</FormLabel>
                          <VStack align="start" spacing={2}>
                            <HStack flexWrap="wrap" spacing={2} align="flex-start">
                              <UploadPanel
                                label="Pravidla soutěže"
                                description="Nahrajte PDF nebo obrázek s pravidly soutěže."
                                accept="image/*,application/pdf"
                                multiple={false}
                                maxFiles={1}
                                uploadFn={uploadRulesFile}
                                allowUrlImport
                                urlPlaceholder="/uploads/... nebo https://example.com/pravidla.pdf"
                                onChange={(files) => {
                                  const last = files[files.length - 1];
                                  if (last?.url) {
                                    setForm((prev: any) => ({ ...prev, rules_url: last.url }));
                                  }
                                }}
                              />
                              <Button variant="outline" onClick={onCreateRulesArticle}>Vytvořit stránku</Button>
                              {form.rules_url && (
                                <Button
                                  as="a"
                                  href={getImageUrl(form.rules_url) || form.rules_url}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  variant="ghost"
                                >
                                  Otevřít
                                </Button>
                              )}
                            </HStack>
                            <Input placeholder="nebo vložte URL" value={form.rules_url} onChange={(e)=>setForm({ ...form, rules_url: e.target.value })} />
                          </VStack>
                        </FormControl>
                      </SimpleGrid>
                    </VStack>
                  </TabPanel>
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <SimpleGrid columns={2} spacing={4}>
                        <FormControl>
                          <FormLabel>Začátek</FormLabel>
                          <DateRangePicker
                            from={form.start_at || undefined}
                            to={form.end_at || undefined}
                            onChange={(fromValue, toValue) => {
                              setForm({ 
                                ...form, 
                                start_at: fromValue || '',
                                end_at: toValue || ''
                              });
                            }}
                          />
                        </FormControl>
                      </SimpleGrid>
                      <SimpleGrid columns={2} spacing={4}>
                        <FormControl>
                          <FormLabel>Styl vizualizace</FormLabel>
                          <Select value={form.picker_style} onChange={(e)=>setForm({ ...form, picker_style: e.target.value })}>
                            <option value="wheel">Kolo štěstí</option>
                            <option value="cycler">Náhodný přepínač</option>
                          </Select>
                        </FormControl>
                        <FormControl isInvalid={Number(form.total_prizes) < 1 || Number(form.total_prizes) > 100}>
                          <FormLabel>Počet výherců</FormLabel>
                          <NumberInput value={String(form.total_prizes ?? '')} min={1} keepWithinRange={false} clampValueOnBlur={false} onChange={(v)=>setForm({ ...form, total_prizes: v })}>
                            <NumberInputField />
                          </NumberInput>
                          <FormHelperText>Max. 100 výherců</FormHelperText>
                        </FormControl>
                      </SimpleGrid>
                      <SimpleGrid columns={3} spacing={4}>
                        <FormControl>
                          <FormLabel>Vstupné (body)</FormLabel>
                          <NumberInput min={0} value={Number(form.entry_cost_points)||0} onChange={(v)=>setForm({ ...form, entry_cost_points: Number(v) || 0 })}>
                            <NumberInputField />
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Max. účastí / uživatel</FormLabel>
                          <NumberInput min={1} keepWithinRange={false} clampValueOnBlur={false} value={String(form.max_entries_per_user ?? '')} onChange={(v)=>setForm({ ...form, max_entries_per_user: v })}>
                            <NumberInputField />
                          </NumberInput>
                        </FormControl>
                      </SimpleGrid>
                    </VStack>
                  </TabPanel>
                  <TabPanel>
                    <VStack align="stretch" spacing={3}>
                      <HStack flexWrap="wrap" spacing={2}>
                        <Button size="sm" onClick={()=>setActiveTab(0)} variant="outline">Zpět na základní</Button>
                        <Button size="sm" onClick={async ()=>{
                          if (!editing) { await ensureCreated(); }
                          setActiveTab(2);
                          setPrizeForm({ ...prizeForm, name: 'Hlavní výhra', quantity: 1, kind: 'physical' });
                          toast({ status:'info', title:'Předvyplněno: Hlavní výhra', description:'Upravte a klikněte Přidat' });
                        }}>1× Hlavní výhra</Button>
                        <Button size="sm" onClick={async ()=>{
                          if (!editing) { await ensureCreated(); }
                          setActiveTab(2);
                          setPrizeForm({ ...prizeForm, name: 'Menší výhra', quantity: 3, kind: 'physical' });
                          toast({ status:'info', title:'Předvyplněno: 3× Menší výhra', description:'Upravte a klikněte Přidat' });
                        }}>3× Menší výhry</Button>
                        <Button size="sm" onClick={async ()=>{
                          if (!editing) { await ensureCreated(); }
                          setActiveTab(2);
                          setPrizeForm({ ...prizeForm, name: '100 bodů', quantity: 10, kind:'points', points: 100 });
                          toast({ status:'info', title:'Předvyplněno: 10× 100 bodů', description:'Upravte a klikněte Přidat' });
                        }}>10× 100 bodů</Button>
                        <Button size="sm" onClick={async ()=>{
                          if (!editing) { await ensureCreated(); }
                          setActiveTab(2);
                          setPrizeForm({ ...prizeForm, name: '500 XP', quantity: 5, kind:'xp', xp: 500 });
                          toast({ status:'info', title:'Předvyplněno: 5× 500 XP', description:'Upravte a klikněte Přidat' });
                        }}>5× 500 XP</Button>
                      </HStack>
                      <Divider />
                      {prizes.length === 0 && <Text color="gray.500">Zatím žádné výhry</Text>}
                      <VStack align="stretch" spacing={2}>
                        {prizes.map((p, i) => (
                          <HStack key={p.id} spacing={2} borderWidth="1px" borderRadius="md" p={2} flexWrap="wrap">
                            <IconButton aria-label="Nahoru" size="xs" icon={<ArrowUpIcon />} onClick={()=>movePrize(i,-1)} flexShrink={0} />
                            <IconButton aria-label="Dolů" size="xs" icon={<ArrowDownIcon />} onClick={()=>movePrize(i,1)} flexShrink={0} />
                            {p.image_url ? (
                              <Image src={getImageUrl(p.image_url) || p.image_url} alt={p.name} boxSize="36px" objectFit="cover" borderRadius="md" flexShrink={0} />
                            ) : null}
                            <VStack align="start" spacing={0} flex={1} minW="250px">
                              <Text fontWeight="600">{p.name}</Text>
                              {p.description && <Text fontSize="xs" color="gray.600" noOfLines={1}>{p.description}</Text>}
                            </VStack>
                            <Text flexShrink={0}>×{p.quantity}</Text>
                            {p.kind && (
                              <Text fontSize="xs" px={2} py={0.5} borderRadius="md" borderWidth="1px" color="gray.600" flexShrink={0}>
                                {p.kind === 'physical' ? 'fyzická' : p.kind === 'points' ? `body ${p.points||0}` : p.kind === 'xp' ? `XP ${p.xp||0}` : `body ${p.points||0} + XP ${p.xp||0}`}
                              </Text>
                            )}
                            <IconButton aria-label="Upravit" size="xs" icon={<EditIcon />} onClick={()=>startEditPrize(p)} flexShrink={0} />
                            <IconButton aria-label="Smazat" size="xs" colorScheme="red" icon={<DeleteIcon />} onClick={()=>delPrize(p)} flexShrink={0} />
                          </HStack>
                        ))}
                      </VStack>
                      <Divider />
                      <Heading size="sm">Přidat výhru</Heading>
                      <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={3} alignItems="end">
                        <FormControl isRequired>
                          <FormLabel>Název</FormLabel>
                          <Input value={prizeForm.name} onChange={(e)=>setPrizeForm({ ...prizeForm, name: e.target.value })} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Počet</FormLabel>
                          <NumberInput min={1} keepWithinRange={false} clampValueOnBlur={false} value={String(prizeForm.quantity ?? '')} onChange={(v)=>setPrizeForm({ ...prizeForm, quantity: v })}>
                            <NumberInputField />
                          </NumberInput>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Obrázek URL</FormLabel>
                          <HStack align="flex-start" spacing={2}>
                            <Input value={prizeForm.image_url} onChange={(e)=>setPrizeForm({ ...prizeForm, image_url: e.target.value })} />
                            <Box flexShrink={0}>
                              <UploadPanel
                                label="Obrázek výhry"
                                description="Nahrajte obrázek výhry."
                                accept="image/*"
                                multiple={false}
                                maxFiles={1}
                                uploadFn={async (file: File): Promise<UploadPanelFile> => {
                                  const r = await uploadFile(file);
                                  setPrizeForm(prev => ({ ...prev, image_url: r.url }));
                                  return {
                                    url: r.url,
                                    name: file.name,
                                    type: file.type || (r as any).type || 'image/*',
                                    size: file.size || (r as any).size || 0,
                                  };
                                }}
                              />
                            </Box>
                          </HStack>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Typ výhry</FormLabel>
                          <Select value={prizeForm.kind || 'physical'} onChange={(e)=>setPrizeForm({ ...prizeForm, kind: e.target.value as any })}>
                            <option value="physical">Fyzická výhra</option>
                            <option value="points">Body</option>
                            <option value="xp">XP</option>
                            <option value="points_xp">Body + XP</option>
                          </Select>
                        </FormControl>
                      </SimpleGrid>
                      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3} alignItems="end">
                        {(prizeForm.kind === 'points' || prizeForm.kind === 'points_xp') && (
                          <FormControl>
                            <FormLabel>Body</FormLabel>
                            <NumberInput min={0} value={Number(prizeForm.points)||0} onChange={(v)=>setPrizeForm({ ...prizeForm, points: Number(v)||0 })}>
                              <NumberInputField />
                            </NumberInput>
                          </FormControl>
                        )}
                        {(prizeForm.kind === 'xp' || prizeForm.kind === 'points_xp') && (
                          <FormControl>
                            <FormLabel>XP</FormLabel>
                            <NumberInput min={0} value={Number(prizeForm.xp)||0} onChange={(v)=>setPrizeForm({ ...prizeForm, xp: Number(v)||0 })}>
                              <NumberInputField />
                            </NumberInput>
                          </FormControl>
                        )}
                      </SimpleGrid>
                      <FormControl>
                        <FormLabel>Popis (volitelné)</FormLabel>
                        <Input value={prizeForm.description || ''} onChange={(e)=>setPrizeForm({ ...prizeForm, description: e.target.value })} />
                      </FormControl>
                      <HStack justify="flex-end">
                        <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={addPrize} isLoading={savingPrize}>Přidat</Button>
                      </HStack>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </ModalBody>
            <ModalFooter>
              <HStack>
                <Button onClick={onClose} variant="ghost">Zavřít</Button>
                <Button colorScheme="blue" onClick={save}>Uložit</Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal isOpen={isPrizeEditOpen} onClose={()=>{ onClosePrizeEdit(); setEditingPrize(null); }} size="lg" isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Upravit výhru</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={3} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <FormControl isRequired>
                    <FormLabel>Název</FormLabel>
                    <Input value={prizeEditForm.name} onChange={(e)=>setPrizeEditForm({ ...prizeEditForm, name: e.target.value })} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Počet</FormLabel>
                    <NumberInput min={1} keepWithinRange={false} clampValueOnBlur={false} value={String(prizeEditForm.quantity ?? '')} onChange={(v)=>setPrizeEditForm({ ...prizeEditForm, quantity: v })}>
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <FormControl>
                    <FormLabel>Obrázek URL</FormLabel>
                    <HStack align="flex-start" spacing={2}>
                      <Input value={prizeEditForm.image_url || ''} onChange={(e)=>setPrizeEditForm({ ...prizeEditForm, image_url: e.target.value })} />
                      <Box flexShrink={0}>
                        <UploadPanel
                          label="Obrázek výhry"
                          description="Nahrajte obrázek výhry."
                          accept="image/*"
                          multiple={false}
                          maxFiles={1}
                          uploadFn={async (file: File): Promise<UploadPanelFile> => {
                            const r = await uploadFile(file);
                            setPrizeEditForm(prev => ({ ...prev, image_url: r.url }));
                            return {
                              url: r.url,
                              name: file.name,
                              type: file.type || (r as any).type || 'image/*',
                              size: file.size || (r as any).size || 0,
                            };
                          }}
                        />
                      </Box>
                    </HStack>
                  </FormControl>
                </SimpleGrid>
                <FormControl>
                  <FormLabel>Popis (volitelné)</FormLabel>
                  <Input value={prizeEditForm.description || ''} onChange={(e)=>setPrizeEditForm({ ...prizeEditForm, description: e.target.value })} />
                </FormControl>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <FormControl>
                    <FormLabel>Typ výhry</FormLabel>
                    <Select value={prizeEditForm.kind || 'physical'} onChange={(e)=>setPrizeEditForm({ ...prizeEditForm, kind: e.target.value as any })}>
                      <option value="physical">Fyzická výhra</option>
                      <option value="points">Body</option>
                      <option value="xp">XP</option>
                      <option value="points_xp">Body + XP</option>
                    </Select>
                  </FormControl>
                  {(prizeEditForm.kind === 'points' || prizeEditForm.kind === 'points_xp') && (
                    <FormControl>
                      <FormLabel>Body</FormLabel>
                      <NumberInput min={0} value={Number(prizeEditForm.points)||0} onChange={(v)=>setPrizeEditForm({ ...prizeEditForm, points: Number(v)||0 })}>
                        <NumberInputField />
                      </NumberInput>
                    </FormControl>
                  )}
                  {(prizeEditForm.kind === 'xp' || prizeEditForm.kind === 'points_xp') && (
                    <FormControl>
                      <FormLabel>XP</FormLabel>
                      <NumberInput min={0} value={Number(prizeEditForm.xp)||0} onChange={(v)=>setPrizeEditForm({ ...prizeEditForm, xp: Number(v)||0 })}>
                        <NumberInputField />
                      </NumberInput>
                    </FormControl>
                  )}
                </SimpleGrid>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <HStack>
                <Button onClick={()=>{ onClosePrizeEdit(); setEditingPrize(null); }} variant="ghost">Zrušit</Button>
                <Button colorScheme="blue" onClick={saveEditPrize} isLoading={savingPrize}>Uložit</Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Container>
    </AdminLayout>
  );
};

export default SweepstakesAdminPage;
